import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { scrollToFirstFormError } from '@/lib/scroll-to-form-error';
import { useFestivalWorkspaceStore } from '@/stores/festival-workspace-store';
import {
  useChangeFestivalStatusMutation,
  useSaveFestivalMutation,
} from '../../queries/festivals/mutations';
import { useFestivalDetailQuery } from '../../queries/useFestivalDetailQuery';
import {
  Button,
  DateFormWidget,
  FestivalHeader,
  FileUpload,
  FormRow,
  Loading,
  Map,
} from '../controls';
import { FestivalPromotionImages } from './FestivalPromotionImages';

const defaultDateObject = {
  startTime: null,
  endTime: null,
  startTimeMS: null,
  endTimeMS: null,
};

const FESTIVAL_STATUSES = [
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'UNPUBLISHED', label: 'Unpublished' },
  { value: 'PREVIEW', label: 'Preview' },
  { value: 'DELETED', label: 'Deleted' },
];

const defaultLinks = {
  instagram: '',
  twitter: '',
  facebook: '',
  web: '',
};

function validateDates(dates) {
  if (!dates?.length) {
    return false;
  }
  const validDates = dates.filter(
    (dateObj) => dateObj.startTimeMS && dateObj.endTimeMS,
  );
  return validDates.length === dates.length;
}

function locationIsSet(location) {
  return (
    location != null &&
    Number.isFinite(Number(location.latitude)) &&
    Number.isFinite(Number(location.longitude))
  );
}

function buildFormValues(festival) {
  return {
    name: festival.name || '',
    description: festival.description || '',
    locationName: festival.location?.locationName ?? '',
    links: { ...defaultLinks, ...(festival.links || {}) },
  };
}

function FestivalFormFields({
  festival,
  patchFestival,
  saveMutation,
  statusMutation,
  loading,
  bridgeLoading,
  readOnly = false,
}) {
  const [workspaceErrors, setWorkspaceErrors] = useState({});
  const formRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: buildFormValues(festival),
    mode: 'onSubmit',
  });

  const watchedName = watch('name');

  useEffect(() => {
    reset(buildFormValues(festival));
  }, [
    festival.festivalId,
    festival.name,
    festival.description,
    festival.location?.locationName,
    festival.links,
    reset,
  ]);

  const getScrollRoot = useCallback(() => {
    return (
      formRef.current?.closest('[data-modal-scroll]') ??
      formRef.current ??
      document
    );
  }, []);

  const scrollToFirstError = useCallback(
    (rhfErrors = {}, workspace = workspaceErrors) => {
      scrollToFirstFormError(getScrollRoot(), [
        { hasError: Boolean(rhfErrors.name), selector: '[name="name"]' },
        {
          hasError: Boolean(workspace.dates),
          selector: '[data-form-field="dates"]',
        },
        {
          hasError: Boolean(workspace.location),
          selector: '[data-form-field="location"]',
        },
        {
          hasError: Boolean(rhfErrors.description),
          selector: '[name="description"]',
        },
      ]);
    },
    [getScrollRoot, workspaceErrors],
  );

  const handleDateChange = useCallback(
    (dates) => {
      patchFestival({ ...festival, dates });
      setWorkspaceErrors((prev) => {
        const next = { ...prev };
        delete next.dates;
        return next;
      });
    },
    [festival, patchFestival],
  );

  const mapCoordinatesChanged = useCallback(
    (location) => {
      patchFestival({
        ...festival,
        location: { ...(festival.location ?? {}), ...location, radius: 1000 },
      });
      setWorkspaceErrors((prev) => {
        const next = { ...prev };
        delete next.location;
        return next;
      });
    },
    [festival, patchFestival],
  );

  const onSubmit = async (values) => {
    const nextWorkspaceErrors = {};
    if (!validateDates(festival.dates)) {
      nextWorkspaceErrors.dates = 'At least one date is required.';
    }
    if (!locationIsSet(festival.location)) {
      nextWorkspaceErrors.location =
        'Location is required. Click the map to set coordinates.';
    }
    if (Object.keys(nextWorkspaceErrors).length > 0) {
      setWorkspaceErrors(nextWorkspaceErrors);
      requestAnimationFrame(() => {
        scrollToFirstFormError(getScrollRoot(), [
          {
            hasError: Boolean(nextWorkspaceErrors.dates),
            selector: '[data-form-field="dates"]',
          },
          {
            hasError: Boolean(nextWorkspaceErrors.location),
            selector: '[data-form-field="location"]',
          },
        ]);
      });
      return;
    }
    setWorkspaceErrors({});

    const updatedFestival = {
      ...festival,
      name: values.name.trim(),
      description: values.description.trim(),
      links: values.links,
      location: {
        ...(festival.location ?? {}),
        locationName: values.locationName.trim(),
      },
    };

    delete updatedFestival.startDate;

    await saveMutation.mutateAsync(updatedFestival);
  };

  const onFileUpload = useCallback(
    (fileInfo) => {
      const { filename, url } = fileInfo;
      patchFestival({ ...festival, photoURL: url, photo: filename });
    },
    [festival, patchFestival],
  );

  const onFileDelete = useCallback(() => {
    patchFestival({ ...festival, photoURL: null, photo: null });
  }, [festival, patchFestival]);

  const handleStatusChange = async (e) => {
    const status = e.target.value;
    const current = festival.status || 'UNPUBLISHED';
    if (!festival.festivalId || status === current) {
      return;
    }
    try {
      await statusMutation.mutateAsync({
        festivalId: festival.festivalId,
        status,
      });
    } catch {
      /* toast via mutation */
    }
  };

  const showLoading = bridgeLoading || loading || isSubmitting;
  const dates = festival.dates || [defaultDateObject];

  const fileList = [];
  if (festival.photoURL) {
    fileList.push({
      uid: '-1',
      name: festival.photo,
      url: festival.photoURL,
      status: 'done',
    });
  }

  if (showLoading && bridgeLoading) {
    return <Loading />;
  }

  const onInvalid = useCallback(
    (rhfErrors) => {
      scrollToFirstError(rhfErrors, workspaceErrors);
    },
    [scrollToFirstError, workspaceErrors],
  );

  const submitForm = handleSubmit(onSubmit, onInvalid);
  const headerTitle =
    festival?.name?.trim() || watchedName?.trim() || 'Add New Event';

  return (
    <div>
      <FestivalHeader
        title={headerTitle}
        onSave={readOnly ? undefined : submitForm}
        showSave={!readOnly}
        showExit={true}
        editHref={
          readOnly && festival.festivalId
            ? `/festival/${festival.festivalId}`
            : undefined
        }
      />
      <form
        ref={formRef}
        className="admin-form"
        onSubmit={submitForm}
        noValidate
      >
        <fieldset disabled={readOnly} className="m-0 min-w-0 border-0 p-0">
          <FormRow label="Festival Image">
            <div className="flex min-w-0 w-full flex-row flex-nowrap items-start gap-5">
              <FileUpload
                fullWidth={false}
                fileList={fileList}
                onFileUpload={onFileUpload}
                onFileDelete={onFileDelete}
                readOnly={readOnly}
              />
              <FestivalPromotionImages
                festivalId={festival.festivalId}
                status={festival.status || 'UNPUBLISHED'}
              />
            </div>
          </FormRow>
          <FormRow label="Event Name" error={errors.name?.message}>
            <input
              type="text"
              placeholder="Type an event name here..."
              {...register('name', { required: 'Name is required' })}
            />
          </FormRow>
          {!!festival.festivalId && (
            <FormRow label="Status">
              <div
                className="flex flex-col gap-2"
                role="radiogroup"
                aria-label="Festival status"
              >
                {FESTIVAL_STATUSES.map(({ value: st, label }) => (
                  <label
                    key={st}
                    className="flex cursor-pointer items-center gap-2 leading-10"
                  >
                    <input
                      type="radio"
                      name="festival-status"
                      value={st}
                      checked={(festival.status || 'UNPUBLISHED') === st}
                      onChange={handleStatusChange}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </FormRow>
          )}
          <DateFormWidget
            label="Event Dates & Times"
            dates={dates}
            handleDateChange={handleDateChange}
            allowMultiple={true}
            readOnly={readOnly}
            datesError={workspaceErrors.dates}
          />
          <FormRow label="Location">
            <input
              type="text"
              placeholder="Type a Location Description Here"
              {...register('locationName')}
            />
          </FormRow>
          <FormRow formField="location" error={workspaceErrors.location}>
            <Map
              location={festival.location}
              handleClick={readOnly ? undefined : mapCoordinatesChanged}
              readOnly={readOnly}
            />
          </FormRow>
          <FormRow
            label="General Information"
            error={errors.description?.message}
          >
            <textarea
              placeholder="Festival Information..."
              {...register('description', {
                required: 'Description is required',
              })}
            />
          </FormRow>
          <FormRow label={<FontAwesomeIcon icon={['fas', 'globe']} />}>
            <input
              type="text"
              placeholder="Website"
              {...register('links.web')}
            />
          </FormRow>
          <FormRow label={<FontAwesomeIcon icon={['fab', 'facebook']} />}>
            <input
              type="text"
              placeholder="Facebook"
              {...register('links.facebook')}
            />
          </FormRow>
          <FormRow label={<FontAwesomeIcon icon={['fab', 'twitter']} />}>
            <input
              type="text"
              placeholder="Twitter"
              {...register('links.twitter')}
            />
          </FormRow>
          <FormRow label={<FontAwesomeIcon icon={['fab', 'instagram']} />}>
            <input
              type="text"
              placeholder="Instagram"
              {...register('links.instagram')}
            />
          </FormRow>
        </fieldset>
        {!readOnly && (
          <div className="mt-8 flex justify-end border-t border-black/[0.08] pt-6 pb-2">
            <Button
              type="submit"
              disabled={isSubmitting || saveMutation.isPending}
              mode="dark"
            >
              Save Festival
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}

export function FestivalForm(props) {
  const festival = useFestivalWorkspaceStore((s) => s.festival);
  const setFestival = useFestivalWorkspaceStore((s) => s.setFestival);
  const patchFestival = useFestivalWorkspaceStore((s) => s.patchFestival);
  const saveMutation = useSaveFestivalMutation();
  const statusMutation = useChangeFestivalStatusMutation();

  const readOnly =
    props.readOnly === true ||
    (props.match?.path != null && props.match.path.endsWith('/view'));

  const id = props.match?.params?.id;
  const numericId =
    id !== undefined && id !== '' && !Number.isNaN(Number(id))
      ? String(id)
      : undefined;

  const q = useFestivalDetailQuery(numericId);

  useEffect(() => {
    if (!props.match || !numericId) {
      setFestival({});
    }
  }, [props.match, numericId, setFestival]);

  useEffect(() => {
    if (q.isSuccess && q.data) {
      setFestival(q.data);
    }
  }, [q.isSuccess, q.data, setFestival]);

  const bridgeLoading = Boolean(numericId && q.isPending);
  const loading = saveMutation.isPending || statusMutation.isPending;

  return (
    <FestivalFormFields
      festival={festival}
      patchFestival={patchFestival}
      saveMutation={saveMutation}
      statusMutation={statusMutation}
      loading={loading}
      bridgeLoading={bridgeLoading}
      readOnly={readOnly}
    />
  );
}
